import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import api from "../api/axios";
import { useAuth } from "../contexts/AuthContext";
import { useQrOwnership } from "../hooks/useIsQrOwner";
import { mockApiService, shouldUseMockData, mockLockets } from "../mockData/mockLocketData";
import { Edit3, LogOut, Lock } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import EmergencyLayout from "../components/layout/EmergencyLayout";
import PremiumLockedBanner from "../components/PremiumLockedBanner";
import PremiumExpiryBadge from "../components/PremiumExpiryBadge";
import ScanConsentModal from "../components/emergency/ScanConsentModal";
import BottomSheet from "../components/ui/bottom-sheet";
import { ErrorState, EmergencyCallStrip } from "../components/ui/states";
import { EmergencyInfoSkeleton } from "../components/ui/skeleton";
import { parseEmergencyProfile, getSavedBystanderReports } from "../utils/essentialFields";
import { useGeolocation } from "../hooks/useGeolocation";
import {
  categorizeDetailSections,
  getSnapshotFields,
  buildMedicalItems,
  buildPersonalItems,
  buildEmergencyDetailItems,
  buildOtherDetailItems,
  buildManagedSubjectProfileItems,
  isManagedSubjectType,
  resolveManagedPrimaryContact,
  extractAge,
  extractGender,
  formatAge,
  formatGender,
} from "../utils/publicProfileUtils";
import PublicManagedSubjectHero from "../components/emergency/public/PublicManagedSubjectHero";
import PublicEmergencyHero from "../components/emergency/public/PublicEmergencyHero";
import PublicQuickActionsBar from "../components/emergency/public/PublicQuickActionsBar";
import PublicCollapsibleSection from "../components/emergency/public/PublicCollapsibleSection";
import PublicDetailRows from "../components/emergency/public/PublicDetailRows";
import PublicEmergencyContacts from "../components/emergency/public/PublicEmergencyContacts";
import PublicStickyCallBar from "../components/emergency/public/PublicStickyCallBar";
import PublicScrollHints from "../components/emergency/public/PublicScrollHints";
import PublicEmergencyFooter from "../components/emergency/public/PublicEmergencyFooter";
import PublicGoodSamaritanSection from "../components/emergency/public/PublicGoodSamaritanSection";
import CallRevealConfirmModal from "../components/emergency/public/CallRevealConfirmModal";
import QrBottomNav from "../components/qr/QrBottomNav";
import MyQrStickerSheet from "../components/qr/MyQrStickerSheet";
import { useCallReveal } from "../hooks/useCallReveal";
import { toTitleCase } from "../utils/titleCase";
import { shouldSkipScanAlert } from "../utils/scanAlertNav";
import { isPreferNotToShare } from "../utils/fieldTypes";

const PublicEmergencyInfoPage = () => {
  const { qrId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { token, logout } = useAuth();
  const { isOwner, checking: ownershipChecking } = useQrOwnership(qrId);
  const skipScanAlert = shouldSkipScanAlert(location.state);
  const { t } = useLanguage();
  const [sections, setSections] = useState([]);
  const [customerName, setCustomerName] = useState("");
  const [subjectType, setSubjectType] = useState("human");
  const [ownerPhoto, setOwnerPhoto] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showReportDisclaimer, setShowReportDisclaimer] = useState(false);
  const [showMyQrSheet, setShowMyQrSheet] = useState(false);
  const [savedReports, setSavedReports] = useState([]);
  const [isPremium, setIsPremium] = useState(false);
  const [premiumExpired, setPremiumExpired] = useState(false);
  const [medicalProfileLocked, setMedicalProfileLocked] = useState(null);
  const [medicalProfile, setMedicalProfile] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [scanConsentPhase, setScanConsentPhase] = useState("idle");
  const [scanShareStarted, setScanShareStarted] = useState(false);
  const [scanAlertStatus, setScanAlertStatus] = useState(null);
  /** Bystander must finish location gate before emergency profile is shown. */
  const [scanGateDone, setScanGateDone] = useState(() => {
    if (skipScanAlert) return true;
    try {
      return Boolean(sessionStorage.getItem(`scan_gate_${qrId}`));
    } catch {
      return false;
    }
  });
  const [offlineCopy, setOfflineCopy] = useState(false);
  const [callingMeta, setCallingMeta] = useState({ targets: [], mode: "express", turnstileSiteKey: null });
  const { location: location_coords, requestLocation, loading: geoLoading } = useGeolocation();
  const {
    beginCallReveal,
    closeModal: closeCallRevealModal,
    modalOpen: callRevealOpen,
    modalPhase: callRevealPhase,
    pendingTarget: callRevealTarget,
    locationStatus: callRevealLocationStatus,
    error: callRevealError,
    turnstileRef,
    continueToLocationStep,
    requestLocationThenCall,
    skipLocationAndCall,
    placeCallNow,
  } = useCallReveal({ qrId, turnstileSiteKey: callingMeta.turnstileSiteKey });
  const scanSentRef = useRef(false);
  const scanFallbackRef = useRef(null);

  useEffect(() => {
    const fetchInfo = async () => {
      setLoading(true);
      setError(null);
      const priorityOrder = { high: 0, medium: 1, low: 2 };
      const applyResponse = (res) => {
        const sorted = (res.data?.sections || [])
          .map((section) => ({
            ...section,
            items: (section.items || []).filter((item) => !isPreferNotToShare(item.value)),
          }))
          .sort((a, b) => {
          const d = (priorityOrder[a.priority] || 1) - (priorityOrder[b.priority] || 1);
          return d !== 0 ? d : a.order - b.order;
        });
        setSections(sorted);
        setCustomerName(res.data?.customer?.name || "");
        setSubjectType(res.data?.customer?.subjectType || "human");
        setOwnerPhoto(res.data?.customer?.photo || null);
        setIsPremium(Boolean(res.data?.isPremium));
        setPremiumExpired(Boolean(res.data?.premiumExpired));
        setMedicalProfileLocked(res.data?.medicalProfileLocked || null);
        setMedicalProfile(res.data?.medicalProfile || null);
        setSubscription(res.data?.subscription || null);
        setCallingMeta(res.data?.calling || { targets: [], mode: "express", turnstileSiteKey: null });
      };

      try {
        if (shouldUseMockData()) {
          applyResponse(await mockApiService.getPublicEmergencyInfo(qrId));
        } else {
          try {
            const res = await api.get(`/emergency/${qrId}/public`);
            setOfflineCopy(res.headers?.["x-emergencyqr-offline"] === "1");
            applyResponse(res);
          } catch (apiErr) {
            const upperId = (qrId || "").toUpperCase();
            const isDemo = Object.keys(mockLockets || {}).includes(upperId);
            const retry =
              isDemo &&
              (!apiErr.response || apiErr.response.status === 404 || apiErr.response.status === 400);
            if (retry) {
              applyResponse(await mockApiService.getPublicEmergencyInfo(qrId));
            } else throw apiErr;
          }
        }

      } catch (err) {
        setError(err.response?.data?.error || "Failed to load emergency info.");
      } finally {
        setLoading(false);
      }
    };
    fetchInfo();
  }, [qrId, token]);

  useEffect(() => {
    setSavedReports(getSavedBystanderReports(qrId));
  }, [qrId]);

  useEffect(() => {
    const handler = () => logout();
    window.addEventListener("tokenExpired", handler);
    return () => window.removeEventListener("tokenExpired", handler);
  }, [logout]);

  const parsedProfile = useMemo(() => parseEmergencyProfile(sections), [sections]);

  const { essentials, primaryContact, otherPhones, detailSections } = useMemo(() => {
    const targets = callingMeta.targets || [];
    if (targets.length) {
      const primary = targets.find((t) => t.role === "primary");
      const others = targets.filter((t) => t.role !== "primary");
      return {
        essentials: parsedProfile.essentials,
        primaryContact: primary ? { id: primary.id, name: primary.label } : null,
        otherPhones: others.map((t) => ({ id: t.id, name: t.label })),
        detailSections: parsedProfile.detailSections,
      };
    }
    return parsedProfile;
  }, [callingMeta.targets, parsedProfile]);

  const { medical, personal, emergency, other } = useMemo(
    () => categorizeDetailSections(detailSections),
    [detailSections]
  );

  const snapshot = useMemo(
    () => getSnapshotFields({ essentials, primaryContact, medicalProfile, isPremium, sections, t }),
    [essentials, primaryContact, medicalProfile, isPremium, sections, t]
  );

  const medicalItems = useMemo(
    () => buildMedicalItems({ medicalProfile, isPremium, medicalSections: medical, essentials, t }),
    [medicalProfile, isPremium, medical, essentials, t]
  );

  const personalItems = useMemo(
    () =>
      buildPersonalItems({
        customerName,
        allSections: sections,
        personalSections: personal,
        essentials,
        t,
      }),
    [customerName, sections, personal, essentials, t]
  );

  const emergencyDetailItems = useMemo(
    () => buildEmergencyDetailItems({ emergencySections: emergency, t }),
    [emergency, t]
  );

  const otherDetailItems = useMemo(
    () => buildOtherDetailItems({ otherSections: other, t }),
    [other, t]
  );

  const isManagedSubject = isManagedSubjectType(subjectType);

  const managedProfileItems = useMemo(
    () => (isManagedSubject ? buildManagedSubjectProfileItems(sections, subjectType) : []),
    [isManagedSubject, sections, subjectType],
  );

  const managedPrimaryContact = useMemo(() => {
    if (!isManagedSubject) return null;
    const fromCalling = snapshot.primaryContact;
    if (fromCalling) return fromCalling;
    return resolveManagedPrimaryContact(sections, subjectType, callingMeta.targets || []);
  }, [isManagedSubject, snapshot.primaryContact, sections, subjectType, callingMeta.targets]);

  const addressValue = essentials.find((entry) => entry.id === "address")?.value || "";
  const ageLabel = useMemo(() => formatAge(extractAge(sections), t), [sections, t]);
  const genderLabel = useMemo(() => formatGender(extractGender(sections), t), [sections, t]);

  const medicalLockedSummary = useMemo(() => {
    if (!medicalProfileLocked?.hasData) return null;
    const parts = [];
    if (medicalProfileLocked.conditionCount) parts.push(`${medicalProfileLocked.conditionCount} conditions`);
    if (medicalProfileLocked.allergyCount) parts.push(`${medicalProfileLocked.allergyCount} allergies`);
    if (medicalProfileLocked.medicationCount) parts.push(`${medicalProfileLocked.medicationCount} medicines`);
    if (medicalProfileLocked.contactCount) parts.push(`${medicalProfileLocked.contactCount} contacts`);
    return parts.join(" · ");
  }, [medicalProfileLocked]);

  const handleCallContact = useCallback(
    (contact) => {
      if (!contact) return;
      if (contact.id && callingMeta.mode !== "verified_bystander") {
        beginCallReveal(contact);
        return;
      }
      if (contact?.number) window.location.href = `tel:+91${contact.number}`;
    },
    [beginCallReveal, callingMeta.mode],
  );

  const handleShareLocation = useCallback(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        const mapsUrl = `https://maps.google.com/?q=${latitude},${longitude}`;
        if (navigator.share) {
          navigator.share({ title: t("public.shareLocation"), url: mapsUrl }).catch(() => {
            window.open(mapsUrl, "_blank", "noopener,noreferrer");
          });
        } else {
          window.open(mapsUrl, "_blank", "noopener,noreferrer");
        }
      },
      () => {},
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  }, [t]);

  const completeScanGate = useCallback(() => {
    try {
      sessionStorage.setItem(`scan_gate_${qrId}`, "1");
    } catch {
      /* ignore */
    }
    setScanGateDone(true);
    setScanConsentPhase("idle");
    setScanShareStarted(false);
  }, [qrId]);

  const sendScan = useCallback(
    async (coords) => {
      if (scanSentRef.current || skipScanAlert || isOwner) return;
      scanSentRef.current = true;
      try {
        const res = await api.post("/scan-events", {
          qrId,
          ...(coords
            ? {
                latitude: coords.latitude,
                longitude: coords.longitude,
                geoAccuracyMeters: coords.accuracy,
                consent: true,
              }
            : {}),
        });
        if (res.data?.paused) return;
        setScanAlertStatus(res.data?.notifications?.dispatched ? "sent" : res.data?.notifications?.reason || "ok");
      } catch (err) {
        setScanAlertStatus(err.response?.data?.error || "failed");
      }
    },
    [qrId, skipScanAlert, isOwner]
  );

  const handleScanConsent = () => {
    window.clearTimeout(scanFallbackRef.current);
    // If GPS never returns, still open the profile and notify the owner without coords.
    scanFallbackRef.current = window.setTimeout(() => {
      sendScan(null);
      completeScanGate();
    }, 30000);
    setScanShareStarted(true);
    setScanConsentPhase("loading");
    requestLocation();
  };

  const dismissScanConsent = () => {
    window.clearTimeout(scanFallbackRef.current);
    sendScan(null);
    completeScanGate();
  };

  // Owner / in-app review skips the location gate. Call-reveal flow is unchanged.
  useEffect(() => {
    if (loading || ownershipChecking) return undefined;
    if (skipScanAlert || isOwner) setScanGateDone(true);
    return undefined;
  }, [loading, ownershipChecking, skipScanAlert, isOwner]);

  useEffect(() => {
    if (!scanShareStarted || geoLoading) return undefined;

    if (location_coords) {
      window.clearTimeout(scanFallbackRef.current);
      setScanConsentPhase("success");
      sendScan(location_coords);
      const timer = window.setTimeout(() => {
        completeScanGate();
      }, 700);
      return () => window.clearTimeout(timer);
    }

    // Permission denied / unavailable — still open profile and alert owner.
    window.clearTimeout(scanFallbackRef.current);
    sendScan(null);
    completeScanGate();
    return undefined;
  }, [scanShareStarted, geoLoading, location_coords, sendScan, completeScanGate]);

  const showEmergencyProfile = scanGateDone || skipScanAlert || isOwner;

  if (loading || ownershipChecking) {
    return (
      <EmergencyLayout showLogo maxWidth="max-w-[1100px]">
        <EmergencyCallStrip className="mb-4" />
        <EmergencyInfoSkeleton />
      </EmergencyLayout>
    );
  }

  if (error) {
    return (
      <ErrorState
        title={t("public.errorTitle")}
        message={error}
        onRetry={() => window.location.reload()}
        emergencyCalls
      />
    );
  }

  // Bystander scan gate: ask for location before revealing emergency details.
  if (!showEmergencyProfile) {
    return (
      <EmergencyLayout showLogo maxWidth="max-w-[1100px]" showLanguage={false}>
        <EmergencyCallStrip className="mb-4" />
        <div className="rounded-xl border border-slate-200 bg-white p-5 text-center space-y-2">
          <p className="text-base font-semibold text-slate-900 m-0">{t("public.scanConsentTitle")}</p>
          <p className="text-sm text-slate-600 m-0">{t("public.scanConsentDescription")}</p>
        </div>
        <ScanConsentModal
          open
          phase={scanConsentPhase}
          onShare={handleScanConsent}
          onDismiss={dismissScanConsent}
          t={t}
        />
      </EmergencyLayout>
    );
  }

  return (
    <EmergencyLayout
      showLogo
      maxWidth="max-w-[1100px]"
      className={`pub-root${token ? " qr-owner-page" : ""}`}
      contentClassName="pt-2 pb-4 sm:pt-3 sm:pb-6"
      subHeader={
        <PublicQuickActionsBar
          t={t}
          onReport={() => setShowReportDisclaimer(true)}
          onNearby={() => navigate(`/${qrId}/nearby`)}
          onFirstAid={() => navigate(`/${qrId}/rescue`)}
          onShareLocation={handleShareLocation}
          showResumeReport={savedReports.length > 0}
          onResumeReport={() => navigate(`/${qrId}/report-lookup`)}
        />
      }
      headerRight={
        isOwner ? (
          <button
            type="button"
            onClick={() => logout()}
            className="min-h-12 min-w-12 flex items-center justify-center rounded-lg hover:bg-slate-100"
            aria-label={t("public.logout")}
          >
            <LogOut size={20} />
          </button>
        ) : !ownershipChecking ? (
          <button
            type="button"
            onClick={() => navigate(`/${qrId}/edit`)}
            className="min-h-10 sm:min-h-12 flex items-center gap-1.5 px-2 sm:px-3 rounded-lg border border-slate-300 text-xs sm:text-sm font-semibold whitespace-nowrap text-slate-800 hover:bg-slate-100"
          >
            <Lock size={18} aria-hidden="true" />
            {t("emergency.ownerLogin")}
          </button>
        ) : null
      }
    >
      <div
        className={`pub-page pub-page--has-sticky${token ? " pub-page--has-bottom-nav" : ""}`}
      >
        {offlineCopy && (
          <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900" role="status">
            You're offline. This is the last saved copy of this emergency information and may be out of date.
          </div>
        )}

        {isOwner && isPremium && subscription && (
          <PremiumExpiryBadge subscription={subscription} qrId={qrId} className="mb-6 w-full" />
        )}

        {isOwner && premiumExpired && (
          <PremiumLockedBanner
            title={t("public.premiumSectionLocked")}
            description={t("public.premiumSectionLockedDesc")}
            showUpgrade={isOwner}
            compact
            qrId={qrId}
            className="mb-6"
          />
        )}

        {medicalProfileLocked?.hasData && (
          <PremiumLockedBanner
            title={t("premium.medicalLockedTitle")}
            description={medicalLockedSummary || t("premium.medicalLockedDesc")}
            showUpgrade={isOwner}
            qrId={qrId}
            className="mb-6"
          />
        )}

        <div className="pub-layout">
          <div className="pub-block pub-block--hero">
            {isManagedSubject ? (
              <PublicManagedSubjectHero
                subjectType={subjectType}
                name={customerName}
                photo={ownerPhoto}
                profileItems={managedProfileItems}
                primaryContact={managedPrimaryContact}
                onCallContact={handleCallContact}
                t={t}
              />
            ) : (
              <PublicEmergencyHero
                name={customerName}
                photo={ownerPhoto}
                age={ageLabel}
                gender={genderLabel}
                address={addressValue}
                bloodGroup={snapshot.bloodGroup}
                allergies={snapshot.allergies}
                conditions={snapshot.conditions}
                medications={snapshot.medications}
                primaryContact={snapshot.primaryContact}
                onCallContact={handleCallContact}
                showMedical={isPremium}
                t={t}
              />
            )}
          </div>

          {otherPhones.length > 0 && (
            <div className="pub-block pub-block--contacts">
              <PublicEmergencyContacts otherPhones={otherPhones} onCall={handleCallContact} t={t} />
            </div>
          )}

          {!isManagedSubject && isPremium && (
            <div className="pub-block pub-block--medical">
              <PublicCollapsibleSection title={t("public.medicalInformation")} defaultOpen>
                <PublicDetailRows items={medicalItems} onCallNumber={handleCallContact} t={t} />
              </PublicCollapsibleSection>
            </div>
          )}

          {!isManagedSubject && isPremium && (
            <div className="pub-block pub-block--personal">
              <PublicCollapsibleSection title={t("public.personalInformation")} defaultOpen={false}>
                <PublicDetailRows items={personalItems} onCallNumber={handleCallContact} t={t} />
              </PublicCollapsibleSection>
            </div>
          )}

          {!isManagedSubject && emergencyDetailItems.length > 0 && (
            <div className="pub-block pub-block--emergency-details">
              <PublicCollapsibleSection title={t("public.emergencyDetails")} defaultOpen={false}>
                <PublicDetailRows items={emergencyDetailItems} onCallNumber={handleCallContact} t={t} />
              </PublicCollapsibleSection>
            </div>
          )}

          {!isManagedSubject && otherDetailItems.length > 0 && (
            <div className="pub-block pub-block--other">
              <PublicCollapsibleSection title={t("public.otherInformation")} defaultOpen={false}>
                <PublicDetailRows items={otherDetailItems} onCallNumber={handleCallContact} t={t} />
              </PublicCollapsibleSection>
            </div>
          )}

          <div className="pub-block pub-block--samaritan">
            <PublicGoodSamaritanSection onStartRescue={() => navigate(`/${qrId}/rescue`)} />
          </div>
        </div>

        {isOwner && (
          <button
            type="button"
            className="pub-owner-edit"
            onClick={() => navigate(`/${qrId}/info`)}
          >
            <Edit3 size={16} aria-hidden />
            {t("public.editInformation")}
          </button>
        )}

        {scanAlertStatus && (
          <p className="pub-scan-status">{scanAlertStatus}</p>
        )}

        <PublicEmergencyFooter />
      </div>

      <PublicStickyCallBar t={t} />
      <PublicScrollHints hasBottomChrome={Boolean(token)} />

      <BottomSheet
        open={showReportDisclaimer}
        onClose={() => setShowReportDisclaimer(false)}
        title={t("public.reportEmergency")}
        secondaryAction={{ label: t("shared.cancel"), onClick: () => setShowReportDisclaimer(false) }}
        primaryAction={{
          label: t("public.understandReport"),
          onClick: () => {
            setShowReportDisclaimer(false);
            navigate(`/${qrId}/report`);
          },
        }}
      >
        <p className="text-base text-slate-700 mb-3">{t("public.reportDisclaimerIntro")}</p>
        <p className="text-sm text-slate-600">{t("public.govRecognitionDesc")}</p>
      </BottomSheet>

      <CallRevealConfirmModal
        open={callRevealOpen}
        phase={callRevealPhase}
        contactLabel={callRevealTarget?.name ? toTitleCase(callRevealTarget.name) : ""}
        locationStatus={callRevealLocationStatus}
        error={callRevealError}
        reserveBottomChrome={Boolean(token)}
        onClose={closeCallRevealModal}
        onContinue={continueToLocationStep}
        onShareLocation={requestLocationThenCall}
        onSkipLocation={skipLocationAndCall}
        onPlaceCall={placeCallNow}
        turnstileRef={turnstileRef}
        t={t}
      />

      {token ? (
        <>
          <QrBottomNav
            qrId={qrId}
            activeTab="myqr"
            isOwner={isOwner}
            isAuthenticated={Boolean(token)}
            onEmergency={() => setShowReportDisclaimer(true)}
            onMyQr={() => setShowMyQrSheet(true)}
          />
          <MyQrStickerSheet open={showMyQrSheet} onClose={() => setShowMyQrSheet(false)} qrId={qrId} />
        </>
      ) : null}
    </EmergencyLayout>
  );
};

export default PublicEmergencyInfoPage;
